import { useCallback, useMemo, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import {
  activateCompany,
  deactivateCompany,
  fetchAgencyCompanies,
  fetchCompanies,
} from "api/companies"
import { columns, sortByDefault } from "./columns"
import { defaultSort, limitAndOffset } from "api/utils"
import { queryCache, useMutation, useQuery } from "react-query"

import { ReactComponent as CompaniesIcon } from "assets/icon-companies.svg"
import { Company } from "models/company"
import { EmptyState } from "components/EmptyState"
import { Row } from "react-table"
import { Table } from "components/tables"
import { history } from "my-history"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useAgencyId } from "hooks/useAgencyId"

type Props = {
  entity: "market" | "agency"
}

function ListCompanies({ entity = "market" }: Props) {
  const { t } = useI18n()
  const ls = useLocationSearch()
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const { hasAuthority } = useAuthenticated()

  const params = ls
    .map(limitAndOffset)
    .map(defaultSort(sortByDefault))
    .toString()

  const { agencyId } = useAgencyId()
  const q = useQuery(
    ["companies", params, agencyId],
    entity === "agency" ? fetchAgencyCompanies : fetchCompanies,
  )
  const { status, data } = q
  const suppliers = data?.data

  const [activate] = useMutation(activateCompany, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(["companies", params])
    },
    onError: () => setModalInfo(undefined),
  })

  const [deactivate] = useMutation(deactivateCompany, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries(["companies", params])
    },
    onError: () => setModalInfo(undefined),
  })

  const onActivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("company.modal.activate.title"),
        description: t("company.modal.activate.description"),
        submitLabel: t("modal.label.activate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await activate(id),
      })
    },
    [activate, t],
  )

  const onDeactivate = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("company.modal.deactivate.title"),
        description: t("company.modal.deactivate.description"),
        submitLabel: t("modal.label.deactivate"),
        cancelLabel: t("modal.label.cancel"),
        onSubmit: async () => await deactivate(id),
      })
    },
    [deactivate, t],
  )

  const getRowProps = (row: Row<Company>) => {
    const id = row.original.id

    return entity === "market"
      ? {
          onClick: () => history.push(`/market-companies/${id}/profile`),
        }
      : { onClick: () => history.push(`/agency-companies/${id}/profile`) }
  }

  const isModalOpen = Boolean(modalInfo)

  const filters = useMemo(() => {
    // const codeFilterOptions =
    //   Companies?.map((c) => {
    //     return {
    //       value: c.code || "",
    //       label: <CheckListItemLabel secondary={""} primary={c.code} />,
    //     }
    //   }) || []
    // const statusFilterOptions =
    //   ["ACTIVE", "DEACTIVATED"]?.map((status) => {
    //     return {
    //       value: status || "",
    //       label: <CheckListItemLabel secondary={""} primary={status} />,
    //     }
    //   }) || []
    // const companyFilterOptions =
    //   associatedCompanies?.map((companyName) => {
    //     return {
    //       value: companyName || "",
    //       label: <CheckListItemLabel secondary={""} primary={companyName} />,
    //     }
    //   }) || []

    const options = [
      // {
      //   name: "code",
      //   label: t("filters.code"),
      //   options: codeFilterOptions,
      // },
      // {
      //   name: "status",
      //   label: t("filters.status"),
      //   options: statusFilterOptions,
      // },
      // {
      //   name: "companyName",
      //   label: t("filters.companyName"),
      //   options: companyFilterOptions,
      // },
    ] as any
    return options
  }, [])

  return (
    <>
      <div>
        <Table<Company>
          id="company-list-table"
          columns={columns(t, onActivate, onDeactivate, entity, hasAuthority)}
          data={suppliers ?? []}
          filters={filters ?? []}
          total={data?.total ?? 0}
          loading={status === "loading"}
          searchBy="search"
          getRowProps={getRowProps}
          noResults={
            <EmptyState
              title={t("company.noresults.title")}
              icon={<CompaniesIcon />}
            />
          }
        />
      </div>
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </>
  )
}

export default ListCompanies
